import type { BuildInput, CameraPreset, SaunaEngine } from '../../engines/types';
import type { Market } from '../../domain/location/types';
import { toWorldXZ } from '../../domain/viewer/saunaRotation';
import { getT } from '../../i18n';

/** Map well-known camera preset IDs to i18n keys */
function localizeCameraLabel(id: string, t: ReturnType<typeof getT>): string {
  switch (id) {
    case 'hero': return t.camera_exterior;
    case 'side': return t.camera_side;
    case 'interior-peek': return t.camera_interior;
    default: return id;
  }
}

/**
 * Turn a preset with the building.
 *
 * Presets are authored in MODEL space — "SIDA" means a specific facade, not a
 * specific compass direction — so when the building turns, the camera has to
 * turn with it or the same button starts showing a different wall. Applying
 * the same transform the sauna rig applies is what keeps that promise.
 */
function rotatePreset(preset: CameraPreset, rotationDeg: number, layoutKey: string): CameraPreset {
  preset = { ...preset, framing: `${preset.framing ?? ''}:${layoutKey}:${rotationDeg}` };
  if (rotationDeg === 0) return preset;
  const position = preset.position.clone();
  const rotatedPosition = toWorldXZ(position.x, position.z, rotationDeg);
  position.set(rotatedPosition.x, position.y, rotatedPosition.z);

  const target = preset.target.clone();
  const rotatedTarget = toWorldXZ(target.x, target.z, rotationDeg);
  target.set(rotatedTarget.x, target.y, rotatedTarget.z);

  return { ...preset, position, target };
}

export function resolveCameraPresets(
  engine: Pick<SaunaEngine, 'getDefaultCameraPosition' | 'getCameraPresets'>,
  buildInput: BuildInput,
  market: Market = 'se',
  rotationDeg = 0,
): CameraPreset[] {
  const t = getT(market);
  // Re-aim on a layout/placement change, but preserve the user's orbit when
  // dimensions or openings merely recompute the same named view.
  const layoutKey = [buildInput.entryWall, buildInput.glazingStyle,
    buildInput.saunaPosition, buildInput.hasRelaxRoom].join(':');

  const presets = engine.getCameraPresets?.(buildInput);
  if (presets && presets.length > 0) {
    return presets.map((p) => rotatePreset({ ...p, label: localizeCameraLabel(p.id, t) }, rotationDeg, layoutKey));
  }

  const fallback = engine.getDefaultCameraPosition(
    buildInput.dimensions.width,
    buildInput.dimensions.length,
  );

  const fallbackPresets: CameraPreset[] = [
    {
      id: 'hero',
      label: t.camera_exterior,
      position: fallback.position.clone().set(
        fallback.position.x * 0.96,
        fallback.position.y * 0.92,
        fallback.position.z * 0.94,
      ),
      target: fallback.target.clone().setY(fallback.target.y + 0.12),
    },
    {
      id: 'side',
      label: t.camera_side,
      position: fallback.position.clone().set(fallback.position.x * 1.28, fallback.position.y * 0.84, fallback.position.z * 0.08),
      target: fallback.target.clone().setY(fallback.target.y + 0.08),
    },
    {
      id: 'interior-peek',
      label: t.camera_interior,
      position: fallback.position.clone().multiplyScalar(0.5),
      target: fallback.target.clone().setY(fallback.target.y * 1.42),
    },
  ];
  return fallbackPresets.map((preset) => rotatePreset(preset, rotationDeg, layoutKey));
}
